import dayjs from 'dayjs';

export interface ExportColumn<T = any> {
  title: string;
  dataIndex?: string | string[];
  render?: (value: unknown, record: T, index: number) => string | number;
  hideInExport?: boolean; // 是否在导出时隐藏该列
}

export interface ExportSummary {
  label: string; // 合计行标签，如"合计"
  colSpan?: number; // 需要合并的列数
  values: (string | number)[]; // 合计行的值
}

export interface ExportTableOptions<T = any> {
  columns: ExportColumn<T>[];
  dataSource: T[];
  fileName?: string; // 文件名，不包含扩展名
  summary?: ExportSummary; // 合计行（可选）
}

/**
 * 导出表格数据为 CSV 文件
 * @param options 导出配置
 */
export function exportTableToCSV<T = any>(
  options: ExportTableOptions<T>,
): void {
  const { columns, dataSource, fileName, summary } = options;

  try {
    // 过滤掉隐藏的列
    const visibleColumns = columns.filter((col) => !col.hideInExport);

    // 构建表头
    const headers = visibleColumns.map((col) => col.title);

    // 构建数据行
    const rows: string[][] = [headers];

    // 添加数据行
    dataSource.forEach((record, index) => {
      const row: string[] = visibleColumns.map((col) => {
        let value: string | number = '';

        if (col.render) {
          // 使用自定义 render 函数
          const rendered = col.render(
            getNestedValue(record, col.dataIndex),
            record,
            index,
          );
          value = rendered ?? '';
        } else if (col.dataIndex) {
          // 直接获取数据
          value = getNestedValue(record, col.dataIndex) ?? '';
        }

        return String(value);
      });
      rows.push(row);
    });

    // 添加合计行（如果有）
    if (summary) {
      const summaryRow: string[] = [];
      if (summary.colSpan && summary.colSpan > 0) {
        // 添加合计标签，占用指定列数
        summaryRow.push(summary.label);
        for (let i = 1; i < summary.colSpan; i++) {
          summaryRow.push('');
        }
        // 添加合计值
        summary.values.forEach((val) => {
          summaryRow.push(String(val));
        });
      } else {
        // 如果没有指定 colSpan，合计标签占第一列，其余列填充合计值
        summaryRow.push(summary.label);
        summary.values.forEach((val) => {
          summaryRow.push(String(val));
        });
        // 如果合计值数量少于列数，用空字符串填充
        while (summaryRow.length < headers.length) {
          summaryRow.push('');
        }
      }
      rows.push(summaryRow);
    }

    // 转换为 CSV 格式
    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell);
            // 处理包含逗号、引号或换行符的单元格
            if (
              cellStr.includes(',') ||
              cellStr.includes('"') ||
              cellStr.includes('\n')
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(','),
      )
      .join('\n');

    // 添加 BOM 以支持中文
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    // 创建下载链接
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const exportFileName = fileName
      ? `${fileName}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`
      : `导出数据_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
    link.download = exportFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('导出失败:', error);
    throw error;
  }
}

/**
 * 获取嵌套对象的值
 * @param obj 对象
 * @param path 路径，可以是字符串或字符串数组
 */
function getNestedValue(obj: any, path?: string | string[]): unknown {
  if (!path) return '';
  if (typeof path === 'string') {
    return obj[path] ?? '';
  }
  // 处理嵌套路径，如 ['user', 'name']
  let value: unknown = obj;
  for (const key of path) {
    if (value && typeof value === 'object' && key in value) {
      value = (value as Record<string, unknown>)[key];
    } else {
      return '';
    }
  }
  return value ?? '';
}
